/**
 * The one positive chip a learner bubble may carry (W6, D7 / D9) — which one,
 * and whether at all, is decided by `selectSkillChips`; this only renders it.
 *
 *   - `grammar`   "✓ Voz pasiva · 2/3"            quiet lime outline
 *   - `nailed`    "✓ «wait for» · antes te costaba"  legible: lime tint; a tap
 *                 shows the radar focus it beat ("Tu radar: …") when known
 *   - `completed` "✓ Voz pasiva · completada"     filled lime, one soft glow
 *
 * No sound, no toast, no movement of the thread: it is part of the bubble.
 */
import { useState } from 'react';
import { IconCheck } from '../icons.js';
import type { UiLocale } from '../coachCatalog.js';
import type { SkillChipModel } from '../skillChips.js';
import { SKILL_CHIPS_COPY } from '../skillChipsCopy.js';
import './skillChip.css';

interface SkillChipProps {
  chip: SkillChipModel;
  locale?: UiLocale;
}

export function SkillChip({ chip, locale = 'es' }: SkillChipProps) {
  const copy = SKILL_CHIPS_COPY[locale];
  const [open, setOpen] = useState(false);

  if (chip.kind === 'nailed') {
    const text = copy.nailed(chip.headline, chip.quoted);
    const aria = copy.nailedAria(chip.quoted ? chip.headline : chip.label);
    const content = (
      <>
        <IconCheck size={11} />
        <span className="skill-chip-text">{text}</span>
      </>
    );
    // Without a focus label there is nothing more to show: a plain chip.
    if (!chip.focusLabel) {
      return (
        <span className="skill-chip is-nailed" role="note" aria-label={aria} title={text}>
          {content}
        </span>
      );
    }
    return (
      <span className="skill-chip-wrap">
        <button
          type="button"
          className={`skill-chip is-nailed${open ? ' is-open' : ''}`}
          aria-label={aria}
          aria-expanded={open}
          title={text}
          onClick={() => setOpen((v) => !v)}
        >
          {content}
        </button>
        {open && <span className="skill-chip-context">{copy.focusContext(chip.focusLabel)}</span>}
      </span>
    );
  }

  const text =
    chip.kind === 'completed'
      ? copy.completed(chip.label)
      : copy.grammar(chip.label, chip.reps, chip.repsToComplete);
  const aria =
    chip.kind === 'completed'
      ? copy.completedAria(chip.label)
      : copy.grammarAria(chip.label, chip.reps, chip.repsToComplete);
  return (
    <span className={`skill-chip is-${chip.kind}`} role="note" aria-label={aria} title={text}>
      <IconCheck size={11} />
      <span className="skill-chip-text">{text}</span>
    </span>
  );
}
